/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { set } from "date-fns";
import dayjs from "dayjs";

import { CalendarData, CalendarFinalResponseType } from "~/interface/calendar/calendar";
import { getSession, setSession } from "~/lib/helperFuncation/sessionStorageService";
import { MstPersonType } from "~/type";
import API_PATH from "~/utils/api-routes";
import { SELECTED_DATE, SELECTED_DATE_DATA, TODAY_CALENDAR_DATE } from "~/utils/constant";
import api from "~/utils/Interceptor";
export interface InitCalendarType {
  selectedDate: string;
  calendarData: CalendarData[];
  isLoading: boolean;
  selectedMonth: string;
  start_date: string;
  end_date: string;
  nakashatraData: MstPersonType[];
  chandraData: MstPersonType[];
  todayData: CalendarData | null;
}

// get calendar data
export const getCalendarData = createAsyncThunk<CalendarFinalResponseType, { start_date: string; end_date: string; lang?: string }>(
  "calendar/getCalendarData",
  async (params, { rejectWithValue }) => {
    const { lang = "en", ...dateParams } = params;
    const response = await api.get(API_PATH.NIRNAY, {
      params: { lang, ...dateParams },
    });
    return response.data;
  },
);

export const getAllEvent = createAsyncThunk<CalendarFinalResponseType, { start_date: string; end_date: string; lang?: string }>(
  "calendar/getAllEvent",
  async (params, { rejectWithValue }) => {
    const { lang = "en", ...dateParams } = params;
    const response = await api.get(API_PATH.NIRNAY, {
      params: { lang, ...dateParams },
    });
    return response.data;
  },
);

const _restoredTodayData: CalendarData | null = (() => {
  if (typeof window === "undefined") return null;
  try {
    const saved = getSession(TODAY_CALENDAR_DATE);
    return saved ? (JSON.parse(saved) as CalendarData) : null;
  } catch {
    return null;
  }
})();

const initialState: InitCalendarType = {
  selectedDate: dayjs().format("YYYY-MM-DD"),
  calendarData: [],
  isLoading: false,
  selectedMonth: dayjs().format("YYYY-MM"),
  start_date: "",
  end_date: "",
  nakashatraData: [],
  chandraData: [],
  todayData: _restoredTodayData,
};

// create a slice
export const calendarSlice = createSlice({
  name: "calendar",
  initialState,
  reducers: {
    // set selected date
    setSelectedDate: (state, action: PayloadAction<string>) => {
      state.selectedDate = action.payload;
      if (typeof window !== "undefined") {
        setSession(SELECTED_DATE, action.payload);
        const selectedDateData = state.calendarData.find((data) => data.date === action.payload);
        setSession(SELECTED_DATE_DATA, JSON.stringify(selectedDateData ?? null));
      }
    },
    setCalendarData: (state, action: PayloadAction<CalendarData[]>) => {
      state.calendarData = action.payload;
      // Persist today's data whenever it appears in the loaded set
      const today = dayjs().format("YYYY-MM-DD");
      const found = action.payload.find((d) => d.date === today);
      if (found) {
        state.todayData = found;
        if (typeof window !== "undefined") {
          setSession(TODAY_CALENDAR_DATE, JSON.stringify(found));
        }
      }
    },
    setSelectedMonth: (state, action: PayloadAction<string>) => {
      state.selectedMonth = action.payload;
    },
    setStartDate: (state, action: PayloadAction<string>) => {
      state.start_date = action.payload;
    },
    setEndDate: (state, action: PayloadAction<string>) => {
      state.end_date = action.payload;
    },
    setNakashatraData: (state, action: PayloadAction<MstPersonType[]>) => {
      state.nakashatraData = action.payload;
    },
    setChandraData: (state, action: PayloadAction<MstPersonType[]>) => {
      state.chandraData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCalendarData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCalendarData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.calendarData = action.payload.data.rows;
        // Persist today's data whenever it appears in the loaded set
        const today = dayjs().format("YYYY-MM-DD");
        const found = action.payload.data.rows.find((d) => d.date === today);
        if (found) {
          state.todayData = found;
          if (typeof window !== "undefined") {
            setSession(TODAY_CALENDAR_DATE, JSON.stringify(found));
          }
        }
      })
      .addCase(getCalendarData.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const { setSelectedDate, setCalendarData, setSelectedMonth, setStartDate, setEndDate, setNakashatraData, setChandraData } =
  calendarSlice.actions;
export default calendarSlice.reducer;
